import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import {
  isValidPhone,
  LICENCE_DOCUMENT_MAX_BYTES,
  LICENCE_DOCUMENT_TYPES,
  StaffInviteError,
  staffInviteService,
  type LicenceTypeOption,
  type StaffInviteDetails,
  type StaffInviteFailure,
} from '../../services/staffInviteService';
import { sanitizeMultilineText, sanitizeText } from '../../utils/sanitize';
import './StaffInvitePage.css';

/**
 * Where the emailed staff invite lands. The account already exists and may
 * already be on the roster; this page is the person finishing it. Anybody
 * who will work shifts gets a second step for their licence. Somebody with a
 * management role then goes into the workspace; a guard without one is sent
 * to the mobile app. Holding both gets both.
 */

type Step = 'details' | 'licence' | 'done';

const MAX_NAME_LENGTH = 80;
const todayIso = (): string => new Date().toISOString().slice(0, 10);

export default function StaffInvitePage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState<StaffInviteDetails | null>(null);
  const [failure, setFailure] = useState<StaffInviteFailure | 'unavailable' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [step, setStep] = useState<Step>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [licenceTypes, setLicenceTypes] = useState<LicenceTypeOption[]>([]);
  const [licenceTypesState, setLicenceTypesState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [holderName, setHolderName] = useState('');
  const [licenceTypeId, setLicenceTypeId] = useState('');
  const [licenceNumber, setLicenceNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [licenceDocument, setLicenceDocument] = useState<File | null>(null);
  const [licenceNotes, setLicenceNotes] = useState('');
  const [licenceSubmitted, setLicenceSubmitted] = useState(false);

  const isGuard = invite?.is_guard === true;
  // The tier is 'guard' only for somebody with no management role at all.
  const hasWorkspace = Boolean(invite) && invite?.role !== 'guard';

  useEffect(() => {
    if (!token) {
      setFailure('invalid');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const details = await staffInviteService.fetchInvite(token);
        if (cancelled) return;
        setInvite(details);
        setFirstName(details.first_name);
        setLastName(details.last_name);
      } catch (err) {
        if (cancelled) return;
        setFailure(err instanceof StaffInviteError ? err.reason : 'unavailable');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (step !== 'licence') return undefined;

    let cancelled = false;
    void staffInviteService.fetchLicenceTypes()
      .then((types) => {
        if (cancelled) return;
        setLicenceTypes(types);
        setLicenceTypesState(types.length > 0 ? 'ready' : 'unavailable');
      })
      .catch(() => {
        if (!cancelled) setLicenceTypesState('unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [step]);

  const licenceTypesByAuthority = useMemo(() => {
    const groups = new Map<string, LicenceTypeOption[]>();
    licenceTypes.forEach((type) => {
      groups.set(type.authority, [...(groups.get(type.authority) ?? []), type]);
    });
    return Array.from(groups.entries());
  }, [licenceTypes]);

  const failureCopy = (reason: StaffInviteFailure | 'unavailable'): { title: string; body: string } => {
    switch (reason) {
      case 'accepted':
        return {
          title: t('This invite has already been used'),
          body: t('Your account is set up. Sign in with your email and the password you chose.'),
        };
      case 'expired':
        return {
          title: t('This invite has expired'),
          body: t('Invite links work for seven days. Ask whoever invited you to send a new one.'),
        };
      case 'revoked':
        return {
          title: t('This invite is no longer active'),
          body: t('Ask whoever invited you to check your account and send a new invite.'),
        };
      case 'unavailable':
        return {
          title: t('We could not load your invite'),
          body: t('Check your connection and open the link from your email again.'),
        };
      default:
        return {
          title: t('This invite link is not valid'),
          body: t('Open the link from your most recent invite email. An earlier link stops working when a new invite is sent.'),
        };
    }
  };

  const handleDetailsSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!invite || !token || isSubmitting) return;

    const cleanFirstName = sanitizeText(firstName, MAX_NAME_LENGTH);
    const cleanLastName = sanitizeText(lastName, MAX_NAME_LENGTH);
    const cleanPhone = sanitizeText(phone, 32);

    if (!cleanFirstName || !cleanLastName) {
      setError(t('First name and last name are required'));
      return;
    }
    if (isGuard && !cleanPhone) {
      setError(t('A phone number is required'));
      return;
    }
    if (cleanPhone && !isValidPhone(cleanPhone)) {
      setError(t('Enter a valid phone number'));
      return;
    }
    if (password.length < 8) {
      setError(t('Password must be at least 8 characters'));
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError(t('Password must contain at least one uppercase letter'));
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError(t('Password must contain at least one lowercase letter'));
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError(t('Password must contain at least one number'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('Passwords do not match'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const accepted = await staffInviteService.acceptInvite({
        token,
        firstName: cleanFirstName,
        lastName: cleanLastName,
        phone: cleanPhone,
        password,
      });

      // A failed sign-in here is not an error: the account is finished either
      // way, and the last screen says how to sign in.
      const signedIn = await staffInviteService.signIn(accepted.email || invite.email, password);
      setPassword('');
      setConfirmPassword('');

      if (!accepted.is_guard) {
        if (signedIn) {
          navigate('/', { replace: true });
          return;
        }
        setStep('done');
        return;
      }

      // The licence step uploads into the guard's own folder, which needs
      // their session. Without one it is left for the app.
      setHolderName(`${cleanFirstName} ${cleanLastName}`);
      setStep(signedIn ? 'licence' : 'done');
    } catch (err) {
      if (err instanceof StaffInviteError) {
        setFailure(err.reason);
      } else {
        setError((err as Error).message || t('Failed to accept invite'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * A guard has no use for a web session: the workspace turns them away. One
   * who also holds a management role keeps theirs and walks straight in.
   */
  const finishGuardSetup = async () => {
    if (hasWorkspace) {
      navigate('/', { replace: true });
      return;
    }
    await staffInviteService.signOut().catch(() => undefined);
    setStep('done');
  };

  const handleLicenceFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setError('');

    if (file && !LICENCE_DOCUMENT_TYPES[file.type]) {
      setLicenceDocument(null);
      event.target.value = '';
      setError(t('Upload your licence as a JPG, PNG, WebP or PDF file.'));
      return;
    }
    if (file && file.size > LICENCE_DOCUMENT_MAX_BYTES) {
      setLicenceDocument(null);
      event.target.value = '';
      setError(t('That file is too large. Upload a file smaller than 10 MB.'));
      return;
    }

    setLicenceDocument(file);
  };

  const handleLicenceSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    const cleanHolderName = sanitizeText(holderName, 160);
    const cleanNumber = sanitizeText(licenceNumber, 80);

    if (!cleanHolderName) {
      setError(t('Enter the name printed on your licence'));
      return;
    }
    if (!licenceTypeId) {
      setError(t('Choose your licence type'));
      return;
    }
    if (!cleanNumber) {
      setError(t('Enter your licence number'));
      return;
    }
    if (!expiryDate || expiryDate <= todayIso()) {
      setError(t('Enter an expiry date in the future'));
      return;
    }
    if (issueDate && issueDate > todayIso()) {
      setError(t('The issue date cannot be in the future'));
      return;
    }
    if (!licenceDocument) {
      setError(t('Upload a photo or scan of your licence'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await staffInviteService.submitLicence({
        holderName: cleanHolderName,
        licenceNumber: cleanNumber,
        licenceTypeId,
        issueDate: issueDate || null,
        expiryDate,
        document: licenceDocument,
        notes: sanitizeMultilineText(licenceNotes, 1000) || null,
      });
      setLicenceSubmitted(true);
      await finishGuardSetup();
    } catch (err) {
      setError((err as Error).message || t('Your licence could not be submitted. You can add it later in the app.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-layout staff-invite__loading">
        <div className="spinner" />
      </div>
    );
  }

  if (failure) {
    const copy = failureCopy(failure);
    return (
      <div className="auth-layout staff-invite">
        <div className="auth-layout__lang">
          <LanguageSwitcher />
        </div>
        <div className="auth-card staff-invite__card">
          <h1 className="auth-card__title">{copy.title}</h1>
          <p className="auth-card__subtitle">{copy.body}</p>
          {failure === 'accepted' && (
            <button type="button" className="btn btn--primary btn--full-width" onClick={() => navigate('/')}>
              {t('Go to sign in')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!invite) return null;

  const companyName = invite.company_name ?? 'OdinsEye';

  if (step === 'done') {
    return (
      <div className="auth-layout staff-invite">
        <div className="auth-layout__lang">
          <LanguageSwitcher />
        </div>
        <div className="auth-card staff-invite__card">
          <h1 className="auth-card__title">{t('You are all set')}</h1>
          {isGuard ? (
            <>
              <p className="auth-card__subtitle">
                {t('Download the OdinsEye app from the App Store or Google Play and sign in with {email} and the password you just chose. Your shifts for {company} will be waiting for you.', {
                  email: invite.email,
                  company: companyName,
                })}
              </p>
              <p className="staff-invite__note">
                {licenceSubmitted
                  ? t('Your licence has been sent to {company} for review.', { company: companyName })
                  : t('You can add your licence at any time from the app.')}
              </p>
              {hasWorkspace && (
                <button type="button" className="btn btn--primary btn--full-width" onClick={() => navigate('/')}>
                  {t('Go to sign in')}
                </button>
              )}
            </>
          ) : (
            <>
              <p className="auth-card__subtitle">
                {t('Your account is ready. Sign in with {email} and the password you just chose.', { email: invite.email })}
              </p>
              <button type="button" className="btn btn--primary btn--full-width" onClick={() => navigate('/')}>
                {t('Go to sign in')}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (step === 'licence') {
    const noTypes = licenceTypesState === 'unavailable';
    return (
      <div className="auth-layout staff-invite">
        <div className="auth-layout__lang">
          <LanguageSwitcher />
        </div>
        {/* Keyed so the details form is torn down rather than patched into this
            one: its password field would otherwise be reused as another input. */}
        <form key="licence" onSubmit={handleLicenceSubmit} className="auth-card staff-invite__card" noValidate>
          <p className="staff-invite__step">{t('Step {current} of {total}', { current: 2, total: 2 })}</p>
          <h1 className="auth-card__title">{t('Add your licence')}</h1>
          <p className="auth-card__subtitle">
            {t('{company} checks your licence before putting you on licensed duties. Add it now, or skip this and add it later in the app.', {
              company: companyName,
            })}
          </p>

          <div aria-live="polite">
            {error && <div className="msg--error">{error}</div>}
            {noTypes && (
              <div className="msg--error">
                {t('Licence types could not be loaded. Skip this step and add your licence in the app.')}
              </div>
            )}
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="staff-invite-holder">{t('Name on licence')}</label>
            <input
              id="staff-invite-holder"
              type="text"
              className="form__input"
              value={holderName}
              onChange={(event) => setHolderName(event.target.value)}
              autoComplete="name"
            />
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="staff-invite-licence-type">{t('Licence Type')}</label>
            <select
              id="staff-invite-licence-type"
              className="form__select"
              value={licenceTypeId}
              onChange={(event) => setLicenceTypeId(event.target.value)}
              disabled={licenceTypesState !== 'ready'}
            >
              <option value="">{t('Choose a licence type')}</option>
              {licenceTypesByAuthority.map(([authority, types]) => (
                <optgroup key={authority} label={authority}>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="staff-invite-licence-number">{t('Licence Number')}</label>
            <input
              id="staff-invite-licence-number"
              type="text"
              className="form__input"
              value={licenceNumber}
              onChange={(event) => setLicenceNumber(event.target.value)}
              autoComplete="off"
            />
          </div>

          <div className="staff-invite__grid">
            <div className="form__group">
              <label className="form__label" htmlFor="staff-invite-issue-date">
                {t('Issue Date')} <span className="staff-invite__optional">{t('(optional)')}</span>
              </label>
              <input
                id="staff-invite-issue-date"
                type="date"
                className="form__input"
                value={issueDate}
                max={todayIso()}
                onChange={(event) => setIssueDate(event.target.value)}
              />
            </div>
            <div className="form__group">
              <label className="form__label" htmlFor="staff-invite-expiry-date">{t('Expiry Date')}</label>
              <input
                id="staff-invite-expiry-date"
                type="date"
                className="form__input"
                value={expiryDate}
                min={todayIso()}
                onChange={(event) => setExpiryDate(event.target.value)}
              />
            </div>
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="staff-invite-document">{t('Photo or scan of your licence')}</label>
            <input
              id="staff-invite-document"
              type="file"
              className="staff-invite__file"
              accept={Object.keys(LICENCE_DOCUMENT_TYPES).join(',')}
              onChange={handleLicenceFile}
            />
            <p className="staff-invite__hint">{t('JPG, PNG, WebP or PDF, up to 10 MB.')}</p>
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="staff-invite-notes">
              {t('Notes')} <span className="staff-invite__optional">{t('(optional)')}</span>
            </label>
            <textarea
              id="staff-invite-notes"
              className="form__textarea staff-invite__notes"
              value={licenceNotes}
              onChange={(event) => setLicenceNotes(event.target.value)}
              rows={3}
            />
          </div>

          <button type="submit" disabled={isSubmitting || noTypes} className="btn btn--primary btn--full-width">
            {isSubmitting ? t('Submitting licence...') : t('Submit licence')}
          </button>
          <button
            type="button"
            className="staff-invite__skip"
            onClick={() => { void finishGuardSetup(); }}
            disabled={isSubmitting}
          >
            {t('Skip for now')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-layout staff-invite">
      <div className="auth-layout__lang">
        <LanguageSwitcher />
      </div>
      <form key="details" onSubmit={handleDetailsSubmit} className="auth-card staff-invite__card" noValidate>
        {isGuard && (
          <p className="staff-invite__step">{t('Step {current} of {total}', { current: 1, total: 2 })}</p>
        )}
        <h1 className="auth-card__title">{t('Finish setting up your account')}</h1>
        <p className="auth-card__subtitle">
          {isGuard && !hasWorkspace
            ? t('{company} has added you to their team as a guard. Confirm your details and choose a password.', { company: companyName })
            : t('{company} has added you to their operations workspace. Confirm your details and choose a password.', { company: companyName })}
        </p>

        <div aria-live="polite">
          {error && <div className="msg--error">{error}</div>}
        </div>

        <div className="staff-invite__grid">
          <div className="form__group">
            <label className="form__label" htmlFor="staff-invite-first-name">{t('First name')}</label>
            <input
              id="staff-invite-first-name"
              type="text"
              className="form__input"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              maxLength={MAX_NAME_LENGTH}
            />
          </div>
          <div className="form__group">
            <label className="form__label" htmlFor="staff-invite-last-name">{t('Last name')}</label>
            <input
              id="staff-invite-last-name"
              type="text"
              className="form__input"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
              maxLength={MAX_NAME_LENGTH}
            />
          </div>
        </div>

        <div className="form__group">
          <label className="form__label" htmlFor="staff-invite-email">{t('Email')}</label>
          <input
            id="staff-invite-email"
            type="email"
            value={invite.email}
            readOnly
            autoComplete="username"
            className="form__input form__input--readonly"
          />
        </div>

        <div className="form__group">
          <label className="form__label" htmlFor="staff-invite-phone">
            {t('Phone number')}
            {!isGuard && <> <span className="staff-invite__optional">{t('(optional)')}</span></>}
          </label>
          <input
            id="staff-invite-phone"
            type="tel"
            className="form__input"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            autoComplete="tel"
            placeholder="+353 87 123 4567"
          />
        </div>

        <div className="form__group">
          <label className="form__label" htmlFor="staff-invite-password">{t('Password')}</label>
          <input
            id="staff-invite-password"
            type="password"
            className="form__input"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
          <p className="staff-invite__hint">
            {t('At least 8 characters, with an uppercase letter, a lowercase letter and a number.')}
          </p>
        </div>

        <div className="form__group staff-invite__group--last">
          <label className="form__label" htmlFor="staff-invite-confirm">{t('Confirm Password')}</label>
          <input
            id="staff-invite-confirm"
            type="password"
            className="form__input"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
          />
        </div>

        <button type="submit" disabled={isSubmitting} className="btn btn--primary btn--full-width">
          {isSubmitting
            ? t('Saving...')
            : isGuard ? t('Continue') : t('Finish and open workspace')}
        </button>
      </form>
    </div>
  );
}
